// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what the Tasks screens show when there is nothing to show, while they load and when
// ClickUp did not answer: the shared notice card (ui/page_notice: a small scene of stacked cards, a
// raised tile with a badge, the title, the text, what to do), quiet wells inside task sections, and
// skeleton rows in the shape of what is coming. The Lens material's values come from its .lens.

import {SkeletonCircle} from '@app/features/app/components/skeleton/SkeletonCircle';
import {SkeletonLine} from '@app/features/app/components/skeleton/SkeletonLine';
import styles from '@app/features/konvo_tasks/components/workspace/shared/TaskNotice.module.css';
import {PageNotice, PageNoticeArea, PageNoticeButton} from '@app/features/ui/page_notice/PageNotice';
import {clsx} from 'clsx';
import type React from 'react';

export type TaskNoticePlace = 'main' | 'panel' | 'phone';

/** A notice card: `main` centred on an empty main area, `panel` compact inside a card or side
 *  panel (tile beside the title, no scene), `phone` the full card on a phone screen. */
export function TaskNotice({
	place,
	icon,
	badge,
	tone,
	title,
	text,
	points,
	actions,
	short = false,
	flx,
}: {
	place: TaskNoticePlace;
	/** Above other content (a folded Closed group): no room-filling height. */
	short?: boolean;
	icon: React.ReactNode;
	badge?: React.ReactNode;
	tone?: 'bad' | 'plain';
	title: string;
	text: string;
	points?: ReadonlyArray<{icon: React.ReactNode; text: string}>;
	actions?: React.ReactNode;
	flx: string;
}) {
	return (
		<div
			className={clsx(
				styles.lensPage,
				styles.host,
				place === 'main' && (short ? styles.short : styles.main),
				place !== 'main' && styles.hosted,
				place === 'phone' && styles.hostedPhone,
				place === 'phone' && styles.phone,
				place === 'panel' && styles.panel,
			)}
		>
			<PageNoticeArea>
				<PageNotice
					icon={icon}
					badge={badge}
					tone={tone}
					title={title}
					text={text}
					points={place === 'panel' ? undefined : points}
					actions={actions}
					layout={place === 'panel' ? 'compact' : 'scene'}
					data-flx={flx}
				/>
			</PageNoticeArea>
		</div>
	);
}

/** A button on a notice card: a raised tile, or the one dark primary. */
export function NoticeButton({
	dark,
	icon,
	children,
	onClick,
	flx,
}: {
	dark?: boolean;
	icon?: React.ReactNode;
	children: React.ReactNode;
	onClick: () => void;
	flx?: string;
}) {
	return (
		<PageNoticeButton ink={dark} onClick={onClick} data-flx={flx}>
			{icon}
			{children}
		</PageNoticeButton>
	);
}

/** A quiet empty line inside a section card (no comments, no time yet): a soft well with a small
 *  raised tile, the line, and at most one small action. */
export function SectionEmpty({
	icon,
	text,
	action,
	flx,
}: {
	icon: React.ReactNode;
	text: string;
	action?: React.ReactNode;
	flx?: string;
}) {
	return (
		<div className={clsx(styles.lensPage, styles.sectionHost)}>
			<div className={styles.section} data-flx={flx}>
				<span className={styles.sectionTile} aria-hidden>
					{icon}
				</span>
				<span className={styles.sectionText}>{text}</span>
				{action}
			</div>
		</div>
	);
}

const NAME_WIDTHS = ['46%', '62%', '38%', '54%', '70%', '42%', '58%'];

const NAME_PX = [260, 340, 210, 300, 380, 230, 320];

/** Desktop list rows while the first page loads: a group pill, then rows on the table's own grid
 *  (`rowClassName` carries its column tracks), one shape per column. */
export function DeskRowsSkeleton({
	label,
	rowClassName,
	cells,
	groups = 2,
}: {
	label: string;
	rowClassName: string;
	cells: ReadonlyArray<'circle' | 'line'>;
	groups?: number;
}) {
	return (
		<div className={styles.deskSkeleton} role="status" aria-busy="true" aria-label={label}>
			{Array.from({length: groups}, (_, group) => (
				<div key={group} className={styles.deskGroup} aria-hidden>
					<div className={styles.deskGroupHead}>
						<SkeletonLine width="12px" height="12px" />
						<SkeletonLine width={group === 0 ? '96px' : '72px'} height="22px" />
						<SkeletonLine width="18px" height="10px" />
					</div>
					{Array.from({length: group === 0 ? 5 : 3}, (_, row) => (
						<div key={row} className={clsx(rowClassName, styles.deskRow)}>
							<span className={styles.deskRing}>
								<SkeletonCircle size="18px" />
							</span>
							<SkeletonLine width={`${NAME_PX[(row + group * 3) % NAME_PX.length]}px`} height="12px" />
							{cells.map((cell, index) =>
								cell === 'circle' ? (
									<SkeletonCircle key={index} size="24px" />
								) : (
									<SkeletonLine key={index} width={`${44 + ((row + index) % 3) * 10}px`} height="10px" />
								),
							)}
							<span />
						</div>
					))}
				</div>
			))}
		</div>
	);
}

/** Phone list rows while they load: a status pill, then a card of task rows. */
export function PhoneRowsSkeleton({label, groups = 2}: {label: string; groups?: number}) {
	return (
		<div className={clsx(styles.lensPage, styles.phoneSkeleton)} role="status" aria-busy="true" aria-label={label}>
			{Array.from({length: groups}, (_, group) => (
				<div key={group} aria-hidden>
					<div className={styles.phoneGroupHead}>
						<SkeletonLine width={group === 0 ? '92px' : '70px'} height="24px" />
						<SkeletonLine width="16px" height="10px" />
					</div>
					<div className={styles.phoneCard}>
						{Array.from({length: group === 0 ? 4 : 2}, (_, row) => (
							<div key={row} className={styles.phoneRow}>
								<SkeletonCircle size="22px" />
								<span className={styles.phoneRowText}>
									<SkeletonLine width={NAME_WIDTHS[(row + group * 2) % NAME_WIDTHS.length]} height="12px" />
									<SkeletonLine width="34%" height="9px" />
								</span>
								<SkeletonCircle size="26px" />
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

/** A task's card while it is read: the title, its status, the properties and a section. */
export function TaskDetailSkeleton({label, phone}: {label: string; phone: boolean}) {
	return (
		<div
			className={clsx(styles.lensPage, styles.taskSkeleton, phone && styles.taskSkeletonPhone)}
			role="status"
			aria-busy="true"
			aria-label={label}
		>
			<div className={styles.taskHead} aria-hidden>
				<SkeletonLine width="82%" height="22px" />
				<SkeletonLine width="44%" height="12px" />
				<div className={styles.taskChips}>
					<SkeletonLine width="118px" height={phone ? '40px' : '32px'} />
					<SkeletonLine width="132px" height={phone ? '40px' : '32px'} />
				</div>
			</div>
			<div className={styles.phoneCard} aria-hidden>
				{[52, 40, 46, 36].map((width, row) => (
					<div key={row} className={styles.taskProp}>
						<SkeletonCircle size="20px" />
						<SkeletonLine width={`${width}px`} height="11px" />
						<span className={styles.grow} />
						<SkeletonLine width={row === 0 ? '52px' : '84px'} height="11px" />
					</div>
				))}
			</div>
			<div className={styles.phoneCard} aria-hidden>
				<div className={styles.taskProse}>
					<SkeletonLine width="30%" height="12px" />
					<SkeletonLine width="94%" height="10px" />
					<SkeletonLine width="88%" height="10px" />
					<SkeletonLine width="56%" height="10px" />
				</div>
			</div>
		</div>
	);
}

/** The space overview's shape: four number cards, the lists card and the side column. */
export function OverviewSkeleton({label, cardClass}: {label: string; cardClass: string}) {
	return (
		<div className={styles.overviewSkeleton} role="status" aria-busy="true" aria-label={label}>
			<div className={styles.overviewStats} aria-hidden>
				{[0, 1, 2, 3].map((index) => (
					<div key={index} className={clsx(cardClass, styles.overviewStat)}>
						<SkeletonLine width="44%" height="11px" />
						<SkeletonLine width="38px" height="28px" />
						<SkeletonLine width="62%" height="10px" />
					</div>
				))}
			</div>
			<div className={styles.overviewRow} aria-hidden>
				<div className={clsx(cardClass, styles.overviewLists)}>
					<SkeletonLine width="80px" height="14px" />
					{[0, 1, 2, 3, 4].map((row) => (
						<div key={row} className={styles.overviewListRow}>
							<SkeletonCircle size="18px" />
							<SkeletonLine width={NAME_WIDTHS[row]} height="12px" />
							<span className={styles.grow} />
							<SkeletonLine width="38%" height="8px" />
							<SkeletonLine width="24px" height="10px" />
						</div>
					))}
				</div>
				<div className={styles.overviewSide}>
					{[3, 4].map((rows, card) => (
						<div key={card} className={clsx(cardClass, styles.overviewSideCard)}>
							<SkeletonLine width={card === 0 ? '46%' : '38%'} height="14px" />
							{Array.from({length: rows}, (_, row) => (
								<div key={row} className={styles.overviewPerson}>
									<SkeletonCircle size="30px" />
									<span className={styles.phoneRowText}>
										<SkeletonLine width={NAME_WIDTHS[row + card]} height="11px" />
										<SkeletonLine width="40%" height="8px" />
									</span>
								</div>
							))}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

/** A phone space page while the workspace is read: its title, three numbers and a card of lists. */
export function SpaceSkeleton({label}: {label: string}) {
	return (
		<div className={clsx(styles.lensPage, styles.phoneSkeleton)} role="status" aria-busy="true" aria-label={label}>
			<div className={styles.spaceTitle} aria-hidden>
				<SkeletonLine width="38px" height="38px" />
				<SkeletonLine width="46%" height="22px" />
			</div>
			<SkeletonLine width="58%" height="11px" />
			<div className={styles.spaceStats} aria-hidden>
				{[0, 1, 2].map((index) => (
					<div key={index} className={styles.spaceStat}>
						<SkeletonLine width="54%" height="10px" />
						<SkeletonLine width="30px" height="22px" />
					</div>
				))}
			</div>
			<div className={styles.phoneGroupHead} aria-hidden>
				<SkeletonLine width="80px" height="11px" />
			</div>
			<div className={styles.phoneCard} aria-hidden>
				{[0, 1, 2, 3].map((row) => (
					<div key={row} className={styles.phoneRow}>
						<SkeletonCircle size="18px" />
						<span className={styles.phoneRowText}>
							<SkeletonLine width={NAME_WIDTHS[row]} height="12px" />
							<SkeletonLine width="80%" height="6px" />
						</span>
						<SkeletonLine width="22px" height="11px" />
					</div>
				))}
			</div>
		</div>
	);
}

/** Comments while the task's detail is read: two rows in the shape of a comment. */
export function CommentsSkeleton({label}: {label: string}) {
	return (
		<div className={styles.comments} role="status" aria-busy="true" aria-label={label}>
			{['72%', '48%'].map((width) => (
				<div key={width} className={styles.comment} aria-hidden>
					<SkeletonCircle size="30px" />
					<span className={styles.phoneRowText}>
						<SkeletonLine width="120px" height="10px" />
						<SkeletonLine width={width} height="10px" />
					</span>
				</div>
			))}
		</div>
	);
}
